import React from 'react';
import { Check, ShieldCheck, BadgeCheck } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatPrice } from '../../lib/billing';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/auth-context';
import { RazorpayPaymentButton } from './RazorpayPaymentButton';
import { RazorpaySubscriptionButton } from './RazorpaySubscriptionButton';

export function PricingPlanCard({
  plan,
  currency = 'INR',
  recommended = false,
  currentPlanKey = null,
  ctaLabel = 'Choose plan',
  loading = false,
  onCta,
  testId = 'pricing-plan-card',
  /**
   * When set (and the caller is a guest), the CTA becomes a "Start with…"
   * button that invokes this callback instead of running the authed
   * checkout. The callback is expected to open a guest-checkout dialog.
   * Preferred over the legacy ``razorpayButtonId`` hosted-button prop
   * which shipped an iframe modal with no visible close X.
   */
  onGuestCheckout = null,
  /**
   * @deprecated Use ``onGuestCheckout`` instead. When set, renders the
   * legacy Razorpay hosted Payment Button widget for guests. Kept for
   * backward compat but no callers should pass this any more.
   */
  razorpayButtonId,
  /**
   * When set AND currency==='USD', renders Razorpay's hosted one-time
   * Payment Button (payment-button.js) in place of the normal CTA — for
   * both guests and logged-in users. Our Razorpay merchant account is
   * INR-only, so we use separately-created USD hosted buttons for the
   * international flow; the webhook auto-provisions the user by email
   * regardless of auth state.
   */
  usdOneTimeButtonId = null,
  /**
   * Same as ``usdOneTimeButtonId`` but for recurring subscription plans
   * (uses subscription-button.js instead of payment-button.js).
   */
  usdSubscriptionButtonId = null,
}) {
  const { user } = useAuth() || {};
  const price = currency === 'INR' ? plan.price_inr : plan.price_usd;
  // Original / MSRP price for a launch discount — rendered strikethrough
  // beside the live price when set higher than `price`. Backend catalog
  // controls this via `original_price_inr` / `original_price_usd`.
  const originalPriceRaw = currency === 'INR' ? plan.original_price_inr : plan.original_price_usd;
  const originalPrice = Number(originalPriceRaw) > Number(price || 0) ? Number(originalPriceRaw) : null;
  const period = plan.billing === 'monthly' ? '/ month' : 'one-time';
  const isCurrent = currentPlanKey === plan.key;
  const isMonthly = plan.billing === 'monthly';
  // USD hosted-button path: applies to BOTH guests and logged-in users
  // whenever the caller supplied a USD button id for this plan. Razorpay
  // handles the checkout in its own iframe and our webhook provisions
  // access by email — so we skip our GuestCheckoutDialog / server-side
  // order flow entirely in USD.
  const usdButtonId = isMonthly ? usdSubscriptionButtonId : usdOneTimeButtonId;
  const useUsdHostedButton = currency === 'USD' && !!usdButtonId && !isCurrent;
  // Guests + a guest-checkout handler → render a plain CTA button that
  // opens our own dialog (proper X close). Falls back to the legacy hosted
  // button only if the deprecated prop is still passed.
  const useGuestDialogCta = !useUsdHostedButton && !!onGuestCheckout && !user;
  const useHostedButton = !useUsdHostedButton && !useGuestDialogCta && !!razorpayButtonId && !user;
  return (
    <Card
      className={cn(
        'relative flex flex-col h-full',
        recommended ? 'border-primary/40 ring-1 ring-primary/20' : 'border-border',
      )}
      data-testid={testId}
    >
      <CardContent className="p-6 sm:p-7 flex flex-col h-full">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-mono-edge text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
              {recommended ? 'RECOMMENDED' : 'ONE-TIME'}
            </p>
            <h3 className="font-display text-2xl mt-1 leading-tight" data-testid={`${testId}-name`}>{plan.label}</h3>
            {plan.tagline && (
              <p className="text-sm text-muted-foreground mt-1">{plan.tagline}</p>
            )}
          </div>
          {isCurrent && <Badge variant="secondary" data-testid={`${testId}-current-badge`}>Current</Badge>}
        </div>

        <div className="mt-6 flex items-end justify-between gap-3 border-t border-border pt-5">
          <div>
            <span className="font-mono-edge text-[10px] tracking-[0.18em] text-muted-foreground">
              {currency}
            </span>
            <div className="flex items-baseline gap-3 flex-wrap mt-1">
              <div className="text-4xl sm:text-5xl font-semibold tracking-[-0.02em] leading-none" data-testid={`${testId}-price`}>
                {formatPrice(price, currency)}
              </div>
              {originalPrice && (
                <div className="flex items-baseline gap-1.5" data-testid={`${testId}-original-price`}>
                  <span className="text-lg font-semibold text-muted-foreground line-through tabular-nums">
                    {formatPrice(originalPrice, currency)}
                  </span>
                  <Badge
                    variant="secondary"
                    className="text-[10px] tracking-[0.14em] font-mono uppercase px-2 py-0.5 bg-primary/10 text-primary border border-primary/20 whitespace-nowrap"
                  >
                    Special
                  </Badge>
                </div>
              )}
            </div>
          </div>
          <span className="text-sm text-muted-foreground pb-1">{period}</span>
        </div>

        <ul className="mt-6 space-y-2.5 text-sm" data-testid-list={`${testId}-features`}>
          {plan.features?.map((f, i) => (
            <li key={i} className="flex items-start gap-2.5" data-testid={`${testId}-feature-${i}`}>
              <Check size={14} className="text-primary mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {/* CTA block — pinned to the bottom of every card via `mt-auto`
            and given a matching min-height so the three plan cards on the
            pricing page all surface their visible payment button in the
            same horizontal band. Razorpay's subscription widget is taller
            than the one-time payment widget which is in turn taller than
            our plain "Enquire Now" button; a fixed slot + `justify-end`
            keeps the button rows aligned regardless. */}
        <div className="mt-auto pt-5 border-t border-border flex flex-col justify-center min-h-[324px] space-y-3" data-testid={`${testId}-cta-block`}>
          {useUsdHostedButton ? (
            // USD hosted Razorpay button (payment for one-time,
            // subscription-widget for monthly). Razorpay handles the
            // iframe checkout end-to-end; our webhook provisions the
            // account by email — works for guests AND logged-in users.
            <div
              className="flex justify-center min-h-[46px]"
              data-testid={`${testId}-usd-razorpay-widget`}
            >
              {isMonthly ? (
                <RazorpaySubscriptionButton
                  buttonId={usdButtonId}
                  testId={`${testId}-usd-subscription-btn`}
                  className="w-full flex justify-center"
                />
              ) : (
                <RazorpayPaymentButton
                  buttonId={usdButtonId}
                  testId={`${testId}-usd-payment-btn`}
                  className="w-full flex justify-center"
                />
              )}
            </div>
          ) : useGuestDialogCta ? (
            // Guest path — plain CTA button opens our own GuestCheckoutDialog
            // (proper X close, click-outside, ESC). Once payment succeeds
            // the webhook auto-provisions the account by email.
            <>
              <Button
                className="w-full"
                variant="default"
                disabled={loading}
                onClick={onGuestCheckout}
                data-testid={`${testId}-guest-cta`}
              >
                {loading ? 'Opening\u2026' : ctaLabel}
              </Button>
              {/* Same-email nudge — see GuestCheckoutDialog for the full
                  rationale. Kept short here so the CTA stays visually
                  dominant. */}
              <p
                className="text-[11px] text-muted-foreground text-center leading-snug"
                data-testid={`${testId}-guest-email-hint`}
              >
                Use the <span className="font-semibold text-foreground">same email</span> at
                checkout and login to avoid creating a duplicate account.
              </p>
            </>
          ) : useHostedButton ? (
            // Legacy hosted Razorpay Payment Button — kept only for backward
            // compat with callers that still pass razorpayButtonId.
            <div className="flex justify-center min-h-[46px]" data-testid={`${testId}-razorpay-widget`}>
              {isCurrent ? (
                <Button className="w-full" variant="secondary" disabled data-testid={`${testId}-cta`}>
                  Current plan
                </Button>
              ) : (
                <RazorpayPaymentButton
                  buttonId={razorpayButtonId}
                  testId={`${testId}-razorpay-btn`}
                  className="w-full flex justify-center"
                />
              )}
            </div>
          ) : (
            <Button
              className="w-full"
              variant="default"
              disabled={loading || isCurrent}
              onClick={onCta}
              data-testid={`${testId}-cta`}
            >
              {isCurrent ? 'Current plan' : loading ? 'Opening Razorpay\u2026' : ctaLabel}
            </Button>
          )}
          {/* Trust micro-row directly under CTA */}
          <div className="flex items-center justify-center gap-3 text-[11px] text-muted-foreground" data-testid={`${testId}-trust-row`}>
            <span className="inline-flex items-center gap-1">
              <ShieldCheck size={12} className="text-[hsl(var(--success))]" strokeWidth={2.4} />
              Razorpay secure
            </span>
            <span className="text-border">|</span>
            <span className="inline-flex items-center gap-1">
              <BadgeCheck size={12} className="text-primary" strokeWidth={2.4} />
              {isMonthly ? 'Cancel anytime' : 'No Auto-pay'}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground text-center">
            {isMonthly ? 'Taxes may apply. Cancel anytime.' : 'Taxes may apply.'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default PricingPlanCard;
